import * as React from 'react';
import { isNetworkError, logError } from '../util';
import { apiErrorToSentryExtras } from '../api';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTypedDispatch, useTypedSelector } from '../reducers';

type Props = {
  error: Error | undefined;
  origin?: string;
  errorInfo?: React.ErrorInfo;
  /**
   * "Try again" button action
   */
  invalidate?: () => void;
  /**
   * If provided, skip self-logging and display this Sentry event id.
   * Use when the error has already been captured (e.g. by Sentry.ErrorBoundary).
   */
  eventId?: string;
};

export default function ScreenError({
  error,
  origin,
  errorInfo,
  invalidate,
  eventId,
}: Props) {
  const dispatch = useTypedDispatch();
  const authUser = useTypedSelector((state) => state.auth.user);
  const [loggedEventId, setLoggedEventId] = React.useState<string | null>(
    eventId ?? null,
  );

  let title = 'Something went wrong';
  let description =
    "An unexpected error occurred. We're looking into it and hope to have things running smoothly again soon!";
  let ignore = false;

  if (isNetworkError(error)) {
    title = 'No internet connection';
    description =
      "We're having trouble reaching our network right now. Check your internet connection and give it another try.";
    ignore = true;
  }

  React.useEffect(() => {
    if (ignore || !error || eventId) return;
    const loggedId = logError(error, `ScreenError.loggable[${origin ?? ''}]`, {
      level: 'fatal',
      extra: {
        ...(errorInfo ? { errorInfo } : {}),
        ...apiErrorToSentryExtras(error),
      },
    });
    setLoggedEventId(loggedId);
  }, [error, ignore, origin, errorInfo, eventId]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        height: '100vh',
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        p={2}
        sx={{ gap: 0.75, maxWidth: 400 }}
      >
        <Typography variant="h6" textAlign="center">
          {title}
        </Typography>
        <Typography textAlign="center">{description}</Typography>
        <Typography variant="body2" color="neutral60" textAlign="center">
          If the problem continues, contact us and reference error code{' '}
          {loggedEventId}
        </Typography>
        <Button
          onClick={() => {
            invalidate?.();
          }}
        >
          Try again
        </Button>
        {authUser && (
          <Button
            onClick={() => {
              dispatch({ type: 'LOGOUT' });
            }}
          >
            Log out
          </Button>
        )}
      </Stack>
    </Box>
  );
}
